import { ComponentProps, CSSProperties, useMemo } from 'react';
import { ZfDropdown, ZfDropdownItem } from '@zafire/react';

export type SelectDropdownOption<TValue extends string> = {
  label: string;
  value: TValue;
};

type ZfDropdownProps = ComponentProps<typeof ZfDropdown>;

type Props<TValue extends string> = {
  options: readonly SelectDropdownOption<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  placeholder?: string;
  hierarchy?: ZfDropdownProps['hierarchy'];
  size?: ZfDropdownProps['size'];
  disabled?: ZfDropdownProps['disabled'];
  style?: CSSProperties;
};

export const RoundDropdown = <TValue extends string>({
  options,
  value,
  onChange,
  placeholder = '',
  hierarchy = 'secondary',
  size = 'medium',
  disabled,
  style,
}: Props<TValue>) => {
  const selectedLabel = useMemo(
    () => options.find((option) => option.value === value)?.label ?? placeholder,
    [options, value, placeholder],
  );

  const triggerStyle = useMemo(
    () =>
      ({
        ...{ '--zf-buttons-border-radius-default': '20px' },
        ...style,
      }) as CSSProperties,
    [style],
  );

  return (
    <ZfDropdown style={triggerStyle} text={selectedLabel} hierarchy={hierarchy} size={size} disabled={disabled}>
      {options.map((option) => (
        <ZfDropdownItem
          key={option.value}
          label={option.label}
          state={option.value === value ? 'selected' : 'default'}
          onClick={() => onChange(option.value)}
        />
      ))}
    </ZfDropdown>
  );
};
